import { useState, useEffect, useMemo } from 'react';
import { Button, Divider, Group, Text } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus, FolderOpen } from 'lucide-react';
import { useHeartRateStore } from '../../store/useHeartRateStore.ts';
import { useAuthUser } from '@store/useAuthStore';
import { useUserDevices } from '../../hooks/useUserDevices';
import PageHeader from '../../components/shared/PageHeader.tsx';
import { useUserConfigurationsQuery } from '../../hooks/queries/useUserConfigurationsQuery';
import type { DeviceConfigurationApi } from '@model/DeviceConfiguration';
import { useUserProductsQuery } from '../../hooks/queries/useUserProductsQuery';
import { useUserVideosQuery } from '../../hooks/queries/useUserVideosQuery';
import { SessionsFilters } from './components/SessionsFilters';
import { SessionsTable } from './components/tables/SessionsTable';
import { PerformanceProfiler } from '../../components/PerformanceProfiler';
import { ManualModal } from './components/ManualModal';
import { useSessionData } from './hooks/useSessionData';
import { useSessionFilters } from './hooks/useSessionFilters';
import { useSessionDelete } from './hooks/useSessionDelete';
import { getSessionTypeFilterLabel } from './utils/sessionTypeDisplay';

export function Sessions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userId = useAuthUser();
  const { devices } = useUserDevices(userId?.id?.toString());

  const isMobile = useMediaQuery('(max-width: 768px)');
  const isTablet = useMediaQuery('(max-width: 1024px)');
  const [
    manualModalOpened,
    { open: openManualModal, close: closeManualModal },
  ] = useDisclosure(false);

  const { events } = useHeartRateStore();
  const [appFilter, setAppFilter] = useState<string | null>(null);
  const [nameSearch, setNameSearch] = useState<string>('');
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [selectedStatuses, setSelectedStatuses] = useState<
    ('in-progress' | 'queued' | 'completed')[]
  >(['in-progress', 'queued']);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const {
    data: configDataRaw = [],
    isLoading: configLoading,
    refetch: configRefetch,
  } = useUserConfigurationsQuery(userId?.id?.toString());

  const { data: videosData = [], isLoading: videosLoading } =
    useUserVideosQuery();

  const { isLoading: productsLoading } = useUserProductsQuery();

  // Convert DeviceConfigurationApi[] to ConfigurationRecord[]
  const configData = useMemo(
    () =>
      (configDataRaw as DeviceConfigurationApi[]).map(config => ({
        ...config,
        config:
          config.config == null
            ? undefined
            : typeof config.config === 'string'
              ? config.config
              : JSON.stringify(config.config),
      })),
    [configDataRaw]
  );

  const { processedData, uniqueTypes, availableStatuses } = useSessionData({
    configData,
    devices,
    events,
    videosData,
  });

  const {
    filteredData,
    tableData,
    debouncedAppFilter,
    debouncedNameSearch,
    debouncedDateRange,
    isFiltering,
  } = useSessionFilters({
    processedData,
    selectedStatuses,
    appFilter,
    nameSearch,
    dateRange,
    page,
    pageSize,
  });

  const { deleteInProgress, confirmDelete } = useSessionDelete({
    configRefetch,
    t,
  });

  useEffect(() => {
    setPage(1);
  }, [
    selectedStatuses,
    debouncedAppFilter,
    debouncedNameSearch,
    debouncedDateRange,
  ]);

  const appOptions = useMemo(() => {
    const baseOption = { value: '', label: 'Tutte le categorie' };
    const typeOptions = uniqueTypes.map(type => ({
      value: type,
      label: getSessionTypeFilterLabel(type),
    }));
    return [baseOption, ...typeOptions];
  }, [uniqueTypes]);

  const hasActiveFilters =
    appFilter ||
    selectedStatuses.length < 3 ||
    nameSearch ||
    dateRange[0] ||
    dateRange[1];

  return (
    <PerformanceProfiler id='Sessions'>
      <PageHeader
        title={t('pa_title', 'GESTIONE SESSIONI IN REALTÀ VIRTUALE')}
        subtitle={
          isMobile
            ? 'Prepara le sessioni VR per gli utenti'
            : 'Prepara qui le sessioni da riprodurre con gli utenti attraverso il visore di Realtà Virtuale'
        }
        actions={
          <Group gap='xs' wrap='wrap'>
            <Button
              onClick={openManualModal}
              size={isMobile ? 'md' : 'sm'}
              variant='subtle'
              leftSection={<FileText size={16} />}
              style={{
                minWidth: isMobile ? '100%' : 'auto',
                padding: isMobile ? '10px 20px' : undefined,
                minHeight: isMobile ? '44px' : undefined,
              }}
            >
              {isMobile ? 'Manuali' : 'Scarica Manuali'}
            </Button>
            <Button
              onClick={() => navigate('/sessions/group')}
              variant='outline'
              size={isMobile ? 'md' : 'sm'}
              leftSection={<FolderOpen size={16} />}
              style={{
                minWidth: isMobile ? '100%' : 'auto',
                padding: isMobile ? '10px 20px' : undefined,
                minHeight: isMobile ? '44px' : undefined,
              }}
            >
              Gruppi sessioni
            </Button>
            <Button
              onClick={() => navigate('/sessions/new')}
              size={isMobile ? 'md' : 'sm'}
              leftSection={<Plus size={16} />}
              style={{
                minWidth: isMobile ? '100%' : 'auto',
                padding: isMobile ? '10px 20px' : undefined,
                minHeight: isMobile ? '44px' : undefined,
              }}
            >
              {isMobile
                ? 'Nuova Sessione'
                : t('pa_create_new', 'Crea una nuova sessione')}
            </Button>
          </Group>
        }
      />
      <Divider mb='md' mt='md' />
      <SessionsFilters
        selectedStatuses={selectedStatuses}
        onStatusChange={setSelectedStatuses}
        searchValue={nameSearch}
        onSearchChange={setNameSearch}
        categoryValue={appFilter}
        onCategoryChange={setAppFilter}
        categoryOptions={appOptions}
        dateRangeValue={dateRange}
        onDateRangeChange={setDateRange}
      />

      <ManualModal opened={manualModalOpened} onClose={closeManualModal} />

      {hasActiveFilters && (
        <Text size='sm' c='dimmed' mb='sm'>
          Mostrando {filteredData.length} sessione
          {filteredData.length !== 1 ? 'i' : ''} su {configData.length} totali
        </Text>
      )}

      <SessionsTable
        data={tableData}
        loading={
          configLoading || videosLoading || productsLoading || isFiltering
        }
        totalRecords={filteredData.length}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        navigate={navigate}
        deleteInProgress={deleteInProgress}
        confirmDelete={confirmDelete}
        isMobile={isMobile}
        isTablet={isTablet}
        t={t}
        selectedStatuses={selectedStatuses}
        availableStatuses={availableStatuses}
        onStatusChange={setSelectedStatuses}
        hasOtherFilters={
          !!debouncedAppFilter ||
          !!debouncedNameSearch ||
          !!debouncedDateRange[0] ||
          !!debouncedDateRange[1]
        }
      />
    </PerformanceProfiler>
  );
}
